import {
  ensureDataLayer,
  isTestOrder,
  pushDirectRawGa4Event,
  pushGa4Event,
  withTestOrderFlag,
  type AnalyticsProperties,
} from "@/lib/analytics-ga4";
import {
  isServerCanonicalEvent,
  sendOwnedAnalyticsEvent,
  sendServerAnalyticsEvent,
} from "@/lib/analytics-server";
import { clientGa4ProjectionFor, isOwnedOnlyClientEvent } from "@/lib/ga4-writer-policy.generated";
import { isBrowserTelemetrySuppressed } from "@/lib/sensitive-route-policy";
import { browserTrafficMarker, isAutomatedBrowser } from "@/lib/synthetic-traffic";
import {
  isBrowserThirdPartyTelemetrySuppressed,
  privacySafeTelemetryPath,
} from "@/lib/telemetry-route-policy";
import { normalizedLandingPath } from "@/lib/landing-attribution-policy";
import { eventIdFromProperties } from "@/lib/beacon-types";

function canonicalAttributionPath(value: unknown): unknown {
  if (typeof value !== "string") return value;
  if (value.startsWith("/")) return normalizedLandingPath(privacySafeTelemetryPath(value));
  if (!/^https?:\/\//i.test(value)) return value;
  try {
    return normalizedLandingPath(privacySafeTelemetryPath(new URL(value).pathname));
  } catch {
    return value;
  }
}

function withCanonicalAttributionPaths(properties: AnalyticsProperties): AnalyticsProperties {
  const canonicalized = { ...properties };
  if (Object.hasOwn(properties, "source_path")) {
    canonicalized.source_path = canonicalAttributionPath(properties.source_path);
  }
  if (Object.hasOwn(properties, "landing_path")) {
    canonicalized.landing_path = canonicalAttributionPath(properties.landing_path);
  }
  return canonicalized;
}

function detectDeviceType(): "Mobile" | "Tablet" | "Desktop" | "Unknown" {
  if (typeof navigator === "undefined") return "Unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|PlayBook|Silk/.test(ua)) return "Tablet";
  if (/Mobi|Android|iPhone|iPod|Opera Mini|IEMobile|webOS|BlackBerry/.test(ua)) return "Mobile";
  return "Desktop";
}

function emitClientGa4(name: string, properties: AnalyticsProperties): void {
  if (clientGa4ProjectionFor(name)) {
    // Governed standard-event projections emit only the standard GA4 name;
    // their source event remains canonical in the owned events_raw stream.
    pushGa4Event(name, properties);
    return;
  }
  if (isOwnedOnlyClientEvent(name)) return;
  pushDirectRawGa4Event(name, properties);
  pushGa4Event(name, properties);
}

function routeBrowserEvent(
  name: string,
  properties: AnalyticsProperties,
  serverCanonical: boolean,
  testOrder: boolean,
  thirdPartySuppressed: boolean
): boolean {
  if (!thirdPartySuppressed) ensureDataLayer().push({ event: name, ...properties });
  if (testOrder) return false;
  if (serverCanonical) {
    sendServerAnalyticsEvent(name, properties);
    return false;
  }
  if (!thirdPartySuppressed) emitClientGa4(name, properties);
  return true;
}

export function trackEvent(name: string, properties?: AnalyticsProperties) {
  if (typeof window === "undefined") return;
  if (isBrowserTelemetrySuppressed() || isAutomatedBrowser()) return;
  const serverCanonical = isServerCanonicalEvent(name);
  const suppliedProperties = withTestOrderFlag(properties) ?? {};
  const eventId = eventIdFromProperties(suppliedProperties);
  const eventProperties = {
    event_source: "client" as const,
    $device_type: detectDeviceType(),
    ...withCanonicalAttributionPaths(suppliedProperties),
    event_id: eventId,
    // Applied last so a monitor can never leak into human KPIs through a caller
    // that supplied another traffic_class. Funnel marts promote this marker to
    // the whole browser client before calculating business rates.
    ...browserTrafficMarker(),
  };
  const testOrder = isTestOrder(eventProperties);
  const thirdPartySuppressed = isBrowserThirdPartyTelemetrySuppressed();
  const shouldSendOwned = routeBrowserEvent(
    name,
    eventProperties,
    serverCanonical,
    testOrder,
    thirdPartySuppressed
  );
  if (!shouldSendOwned) return;
  sendOwnedAnalyticsEvent(name, eventProperties);
}

export function trackScreenView(properties: {
  screen: string;
  screen_id?: string;
  route?: string;
  platform?: "web";
}) {
  trackEvent("screen_view", {
    ...properties,
    screen_id: properties.screen_id ?? properties.screen,
    platform: properties.platform ?? "web",
  });
}

export function trackOwnedAnalyticsOnly(name: string, properties?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  sendOwnedAnalyticsEvent(name, {
    ...withCanonicalAttributionPaths(properties ?? {}),
    ...browserTrafficMarker(),
  });
}
